/**
 * Utility helper functions
 */

/**
 * Convert degrees to radians
 */
export function degToRad(degrees) {
    return degrees * (Math.PI / 180);
}

/**
 * Convert radians to degrees
 */
export function radToDeg(radians) {
    return radians * (180 / Math.PI);
}

/**
 * Generate unique ID
 */
export function generateId() {
    return `swk-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Check if value is a plain object (not a class instance, DOM element,
 * array, or function)
 */
export function isPlainObject(item) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
    const proto = Object.getPrototypeOf(item);
    return proto === Object.prototype || proto === null;
}

/**
 * Deep clone a plain value (objects/arrays). Functions and DOM elements are
 * returned as-is so callbacks and container references are never cloned (F13)
 */
export function deepClone(value) {
    if (Array.isArray(value)) {
        return value.map(deepClone);
    }
    if (isPlainObject(value)) {
        const out = {};
        for (const key of Object.keys(value)) {
            out[key] = deepClone(value[key]);
        }
        return out;
    }
    return value;
}

/**
 * Deep merge objects. Nested default branches are cloned so separate
 * instances never share mutable state (F13)
 */
export function deepMerge(target, source) {
    const output = deepClone(target);
    
    if (isPlainObject(output) && isPlainObject(source)) {
        Object.keys(source).forEach(key => {
            if (isPlainObject(source[key])) {
                output[key] = isPlainObject(output[key])
                    ? deepMerge(output[key], source[key])
                    : deepClone(source[key]);
            } else {
                output[key] = source[key];
            }
        });
    }
    
    return output;
}

/**
 * Check if value is an object
 */
export function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Clamp value between min and max
 */
export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

/**
 * Download file
 */
export function downloadFile(blob, filename) {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
}

/**
 * Get timestamp for file naming
 */
export function getTimestamp() {
    return Date.now();
}

/**
 * Explicit mapping of data attribute keys to canonical config paths (F15).
 * `data-swk-grid-center-color` -> dataset key `swkGridCenterColor` -> `grid.centerColor`
 */
export const SWK_DATA_ATTRIBUTE_MAP = {
    swkWidth: 'width',
    swkHeight: 'height',
    swkViewportBackground: 'viewportBackground',
    swkOutlineColor: 'outlineColor',
    swkOutlineThickness: 'outlineThickness',
    swkGridShow: 'grid.show',
    swkGridSize: 'grid.size',
    swkGridDivisions: 'grid.divisions',
    swkGridCenterColor: 'grid.centerColor',
    swkGridLinesColor: 'grid.linesColor',
    swkCameraMode: 'camera.mode',
    swkCameraFov: 'camera.fov',
    swkCameraNear: 'camera.near',
    swkCameraFar: 'camera.far',
    swkCameraPosition: 'camera.position',
    swkTransformMode: 'transform.mode',
    swkTransformSpace: 'transform.space',
    swkTransformSnapUnit: 'transform.snapUnit',
    swkUiEnabled: 'ui.enabled',
    swkUiPanelsShapes: 'ui.panels.shapes',
    swkUiPanelsProperties: 'ui.panels.properties',
    swkUiPanelsOutliner: 'ui.panels.outliner',
    swkUiPanelsControls: 'ui.panels.controls',
    swkFeaturesGrouping: 'features.grouping',
    swkFeaturesUndo: 'features.undo',
    swkFeaturesExport: 'features.export',
    swkHistoryEnabled: 'history.enabled',
    swkHistoryMaxSize: 'history.maxSize'
};

/**
 * Parse HTML data attributes to config object.
 * Attributes map explicitly to canonical config keys (F15); unknown
 * `swk*` attributes are ignored.
 */
export function parseDataAttributes(element) {
    const config = {};
    const dataset = element.dataset;
    
    for (const key in dataset) {
        if (!key.startsWith('swk')) continue;
        
        const configPath = SWK_DATA_ATTRIBUTE_MAP[key];
        if (!configPath) {
            console.warn(`SWK: Unknown data attribute "data-swk-${key.slice(3).replace(/([A-Z])/g, '-$1').toLowerCase()}" ignored`);
            continue;
        }
        
        const value = dataset[key];
        
        // Parse value
        let parsedValue;
        if (value === 'true') {
            parsedValue = true;
        } else if (value === 'false') {
            parsedValue = false;
        } else if (value.includes(',')) {
            // Handle arrays (e.g., "5,5,5" -> [5,5,5])
            parsedValue = value.split(',').map(v => {
                const trimmed = v.trim();
                return !isNaN(trimmed) ? parseFloat(trimmed) : trimmed;
            });
        } else if (!isNaN(value) && value !== '') {
            parsedValue = parseFloat(value);
        } else {
            parsedValue = value;
        }
        
        // Set nested property
        setNestedProperty(config, configPath, parsedValue);
    }
    
    return config;
}

/**
 * Set nested property in object using dot notation
 */
function setNestedProperty(obj, path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    let target = obj;
    
    for (const key of keys) {
        if (!(key in target)) {
            target[key] = {};
        }
        target = target[key];
    }
    
    target[lastKey] = value;
}

/**
 * Create DOM element with classes
 */
export function createElement(tag, classes = [], attributes = {}) {
    const element = document.createElement(tag);
    
    if (classes.length > 0) {
        element.className = classes.join(' ');
    }
    
    for (const [key, value] of Object.entries(attributes)) {
        element.setAttribute(key, value);
    }
    
    return element;
}

/**
 * Dispose Three.js object properly
 */
export function disposeObject(object) {
    if (!object) return;
    
    if (object.geometry) {
        object.geometry.dispose();
    }
    
    if (object.material) {
        if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
        } else {
            object.material.dispose();
        }
    }
    
    if (object.texture) {
        object.texture.dispose();
    }
}
